/**
 * Algolia InstantSearch customizations (theme asset).
 *
 * Registers on algolia.hooks.initialize before InstantSearch starts:
 * 1) Replace collection_ids filters with collections:"{handle}"
 * 2) Apply facetingAfterDistinct per request — false only when that request
 *    facets variant-level attributes (band/cup/size); product-level facet queries
 *    (color, vendor, price) keep facetingAfterDistinct:true even with band/cup
 *    filters active. Distinct product counts for color/band/cup/size are patched
 *    in algolia_instant_search.js.liquid (setupAlgoliaVariantFacetDistinctCounts).
 */
(function () {
  if (window.isB2B) {
    return;
  }
  if (window.__natoriAlgoliaCustomizationsRegistered) {
    return;
  }
  window.__natoriAlgoliaCustomizationsRegistered = true;

  document.addEventListener('algolia.hooks.initialize', () => {
    if (!window.algoliaShopify?.hooks?.registerHook) {
      console.warn(
        '[Algolia customizations] algoliaShopify.hooks was not available on initialize.'
      );
      return;
    }

    /**
     * Replace collection_ids filters generated by the Shopify integration
     * with the current collection handle.
     *
     * Example:
     * collection_ids:"123456789"
     *
     * Becomes:
     * collections:"bras"
     */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchFiltersString',
      (defaultFilter) => {
        if (
          typeof defaultFilter !== 'string' ||
          !defaultFilter.includes('collection_ids')
        ) {
          return defaultFilter;
        }

        const collectionHandle = getCollectionHandle();

        if (!collectionHandle) {
          return defaultFilter;
        }

        return defaultFilter.replace(
          /collection_ids:"[^"]+"/g,
          `collections:"${escapeAlgoliaFilterValue(collectionHandle)}"`
        );
      }
    );

    /**
     * Wrap the Algolia search client and set facetingAfterDistinct per request.
     * Variant facets (size/band/cup) need facetingAfterDistinct:false so counts
     * include all variant sizes, not one size per distinct product card.
     */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchConfigurationOptions',
      (options) => {
        if (!options?.searchClient?.search) {
          console.warn(
            '[Algolia customizations] InstantSearch search client was not available.'
          );

          return options;
        }

        return {
          ...options,
          searchClient: createSearchClientWithFacetingAfterDistinct(
            options.searchClient
          ),
        };
      }
    );
  });

  function getCollectionHandle() {
    if (window.__natoriAlgoliaCollectionHandle) {
      return String(window.__natoriAlgoliaCollectionHandle);
    }

    const match = window.location.pathname.match(
      /^\/collections\/([^/?#]+)\/?$/i
    );

    return match?.[1] || null;
  }

  function escapeAlgoliaFilterValue(value) {
    return String(value)
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"');
  }

  /**
   * Variant-level facets (size, band, cup) must use facetingAfterDistinct:false.
   * With distinct PLP hits, facetingAfterDistinct:true only counts the single
   * representative variant per product — so e.g. White + Size shows M(1), XL(1)
   * instead of all sizes available in white.
   */
  const VARIANT_LEVEL_FACET_PATTERNS = [
    /^options\.size$/i,
    /^meta\.shopify\.size$/i,
    /^options\.band$/i,
    /^options\.cup$/i,
    /^meta\.shopify\.cup/i,
    /\.size$/i,
    /^size$/i,
    /^band$/i,
    /^cup$/i,
  ];

  function collectFacetNamesFromParams(params) {
    const names = new Set();

    const add = (value) => {
      if (!value) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((entry) => add(entry));
        return;
      }
      if (typeof value === 'string') {
        value.split(',').forEach((part) => {
          const trimmed = part.trim();
          if (trimmed) {
            names.add(trimmed);
          }
        });
      }
    };

    add(params.facets);
    add(params.disjunctiveFacets);

    return names;
  }

  /**
   * Only disable facetingAfterDistinct when this specific request is computing
   * facets for variant-level attributes (band/cup/size). Active band/cup filters
   * in facetFilters must NOT force beforeDistinct on color/vendor/price facet
   * queries — that zeroes out all counts when multiple variant filters are set.
   */
  function requestFacetsIncludeVariantLevel(params) {
    const facetNames = collectFacetNamesFromParams(params || {});
    for (const name of facetNames) {
      if (VARIANT_LEVEL_FACET_PATTERNS.some((pattern) => pattern.test(name))) {
        return true;
      }
    }
    return false;
  }

  function createSearchClientWithFacetingAfterDistinct(searchClient) {
    return {
      ...searchClient,

      search(requests) {
        const updatedRequests = requests.map((request) => {
          if (!request?.params) {
            return request;
          }

          const needsVariantFaceting = requestFacetsIncludeVariantLevel(
            request.params
          );

          const params = {
            ...request.params,
            facetingAfterDistinct: !needsVariantFaceting,
          };

          return {
            ...request,
            params,
          };
        });

        return searchClient.search(updatedRequests);
      },
    };
  }
})();
